import { getImageProps } from "next/image";

interface ServiceFigureProps {
  src: string;
  width: number;
  height: number;
  /** Portrait variant served below 640px, where the wide diagram would be unreadable. */
  mobileSrc: string;
  mobileWidth: number;
  mobileHeight: number;
  alt: string;
  caption: string;
  /** Link text to the full-resolution desktop file, for readers who want to zoom. */
  openFullLabel: string;
}

/**
 * ServiceFigure
 *
 * Diagram with art direction: a wide desktop render and a stacked mobile
 * render of the same content, both run through the Next image optimizer
 * (`getImageProps`), plus a caption and a link to the full-size file.
 * Server component — ships no JS.
 */
export default function ServiceFigure({
  src,
  width,
  height,
  mobileSrc,
  mobileWidth,
  mobileHeight,
  alt,
  caption,
  openFullLabel,
}: ServiceFigureProps) {
  const {
    props: { srcSet: desktopSrcSet, ...desktopProps },
  } = getImageProps({
    src,
    width,
    height,
    alt,
    quality: 80,
    sizes: "(min-width: 1080px) 780px, (min-width: 641px) 90vw, 100vw",
  });

  const {
    props: { srcSet: mobileSrcSet },
  } = getImageProps({
    src: mobileSrc,
    width: mobileWidth,
    height: mobileHeight,
    alt,
    quality: 80,
    sizes: "100vw",
  });

  return (
    <figure className="service-figure">
      <picture>
        <source
          media="(max-width: 640px)"
          srcSet={mobileSrcSet}
          width={mobileWidth}
          height={mobileHeight}
        />
        {/* eslint-disable-next-line @next/next/no-img-element -- art direction via getImageProps */}
        <img
          {...desktopProps}
          alt={alt}
          srcSet={desktopSrcSet}
          loading="lazy"
          decoding="async"
        />
      </picture>
      <figcaption>
        {caption}{" "}
        <a href={src} target="_blank" rel="noopener noreferrer">
          {openFullLabel}
        </a>
      </figcaption>
    </figure>
  );
}
